<script lang="ts" setup>
  import { EvaluateRouteSecurity, Router, SecuredRoute } from '@/helpers/router'
  import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
  import { ChevronRightIcon } from '@heroicons/vue/24/solid'
  import { type RouteRecordRaw, RouterLink } from 'vue-router'
  import CmsService from '@/services/CmsService'
  import { RoleAdminIcon, RoleBetaIcon, RoleDevIcon } from '@/config/icons'
  import type { Component } from 'vue'

  type RouteRecord = RouteRecordRaw & { hide?: boolean }
  const allRoutes = computed<RouteRecord[]>(() => {
    const routes: RouteRecord[] = []
    routes.push(...(Router.options.routes.find((route) => route.name === '__site_navigation__')?.children ?? []))
    //routes.push(...CmsService.getRoutesForName('__site_navigation__').value)
    return routes
  })

  const emit = defineEmits(['link-click', 'refresh-current-page'])

  function isCurrent(route: RouteRecord) {
    const currentRoute = Router.currentRoute.value
    if (route.children && route.children.length > 0) {
      const subMatch = route.children.find((subroute: RouteRecord) => subroute.path === currentRoute.path)
      return !!subMatch
    }
    return currentRoute.path === route.path
  }

  const isCurrentClass = 'bg-gray-50 dark:bg-gray-950'
  const isNotCurrentClass = 'hover:bg-gray-50 dark:hover:bg-gray-950'

  function getMarkIcon(item: RouteRecord): { comp: Component; title: string } | undefined {
    if (item.meta?.markAsAdmin) return { comp: RoleAdminIcon, title: 'Administrator' }
    else if (item.meta?.markAsBeta) return { comp: RoleBetaIcon, title: 'Beta-Test' }
    else if (item.meta?.markAsDev) return { comp: RoleDevIcon, title: 'Developer' }
    else return undefined
    //return { comp: RoleManagementIcon, title: 'Verwaltung' }
  }

  const isSameRoute = (target: RouteRecordRaw) => {
    const currentRoute = Router.currentRoute.value
    const resolved = Router.resolve(target)
    return currentRoute.fullPath === resolved.fullPath
  }

  function handleRouteClick(event: MouseEvent, target: RouteRecordRaw) {
    if (isSameRoute(target)) {
      event.preventDefault()
      useEventBus('same_route_click').emit()
      emit('link-click')
      return
    }

    emit('link-click')
  }
</script>

<template>
  <ul
    class="flex flex-1 flex-col gap-y-7"
    role="list">
    <li>
      <ul
        class="-mx-2 space-y-1"
        role="list">
        <li
          v-for="item in allRoutes.filter((i) => !i.meta?.hide && EvaluateRouteSecurity(i as SecuredRoute))"
          :key="item.path">
          <Divider
            v-if="item.name === 'seperator'"
            :pt="{ root: { class: ['my-0.5'] } }"
            :pt-options="{ mergeSections: false, mergeProps: true }" />
          <template v-else>
            <RouterLink
              v-if="!item.children"
              :class="['group flex gap-x-3 p-2 font-semibold text-sm leading-6 text-gray-700 dark:text-gray-300 rounded-md', isCurrent(item) ? isCurrentClass : isNotCurrentClass]"
              :to="item"
              @click="handleRouteClick($event, item)">
              <span class="flex gap-1 items-center">
                <component
                  :is="getMarkIcon(item)?.comp"
                  v-if="getMarkIcon(item)"
                  v-tooltip="getMarkIcon(item)?.title"
                  class="size-4" />
                <component
                  :is="item.meta?.icon"
                  v-if="item.meta?.icon"
                  class="size-4" />
                {{ item.meta?.title ?? 'Missing .title @ ' + (item.name?.toString() ?? item.path) }}
              </span>
            </RouterLink>

            <Disclosure
              v-else
              v-slot="{ open }"
              :default-open="isCurrent(item)"
              as="div">
              <DisclosureButton :class="['flex items-center w-full text-left  p-2 gap-x-3 font-semibold text-sm leading-6 text-gray-700 dark:text-gray-300 rounded-md', isCurrent(item) ? isCurrentClass : isNotCurrentClass]">
                <span class="flex gap-1 items-center">
                  <component
                    :is="getMarkIcon(item)?.comp"
                    v-if="getMarkIcon(item)"
                    v-tooltip="getMarkIcon(item)?.title"
                    class="size-4" />

                  <component
                    :is="item.meta?.icon"
                    v-if="item.meta?.icon"
                    class="size-4" />

                  {{ item.meta?.title ?? 'Missing .title @ ' + (item.name?.toString() ?? item.path) }}
                </span>
                <ChevronRightIcon
                  :class="[open ? 'rotate-90 text-gray-500' : 'text-gray-300 dark:text-gray-600', 'ml-auto h-5 w-5 shrink-0']"
                  aria-hidden="true" />
              </DisclosureButton>
              <DisclosurePanel
                as="ul"
                class="mt-1 px-2">
                <li
                  v-for="subItem in [...item.children, ...CmsService.getRoutesForName(item.name?.toString()).value].filter((i) => !i.meta?.hide && EvaluateRouteSecurity(i as SecuredRoute))"
                  :key="subItem.path">
                  <Divider
                    v-if="subItem.name === 'seperator'"
                    :pt="{ root: { class: ['my-0.5'] } }"
                    :pt-options="{ mergeSections: false, mergeProps: true }" />
                  <RouterLink
                    v-else
                    :class="['block py-2 pr-2 pl-3 text-sm leading-6 text-gray-700 dark:text-gray-300 rounded-md', isCurrent(item) ? isCurrentClass : isNotCurrentClass]"
                    :to="subItem"
                    as="a"
                    @click="handleRouteClick($event, subItem)">
                    <span class="flex gap-1 items-center">
                      <component
                        :is="getMarkIcon(subItem)?.comp"
                        v-if="getMarkIcon(subItem)"
                        v-tooltip="getMarkIcon(subItem)?.title"
                        class="size-4" />

                      <component
                        :is="subItem.meta?.icon"
                        v-if="subItem.meta?.icon"
                        class="size-4" />
                      {{ subItem.meta?.title ?? 'Missing .title @ ' + (subItem.name?.toString() ?? subItem.path) }}
                    </span>
                  </RouterLink>
                </li>
              </DisclosurePanel>
            </Disclosure>
          </template>
        </li>
      </ul>
    </li>
  </ul>
</template>
